import React, { useState, useEffect } from 'react'
import {
    EyeIcon,
    ArrowLeftIcon,
    PencilIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    CheckCircleIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    IdentificationIcon,
} from '@heroicons/react/20/solid'
import { QrCodeIcon } from '@heroicons/react/24/outline'
import { Input, InputGroup } from 'components/input'
import { Divider } from 'components/divider'
import { Badge } from 'components/badge'
import { MultiSelect } from 'components/multi-select'
import { Select } from 'components/select'
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { CalendarIcon, UserIcon } from '@heroicons/react/24/outline'
import { BoatIcon, ReturnTripIcon } from 'components/icons'
import { BoatBookingImage } from 'components/icons'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { GET_ALL_BOOKINGS_LIST } from 'graphql/booking/queries'
import { GET_ROUTE_TYPE } from 'graphql/route/queries'
import { GET_ROUTES_FOR_REPORTS, SEARCH_LOCATION } from 'graphql/reports/queries'
import { useLazyQuery } from '@apollo/client'
import loadingImage from '../../assets/images/boat-loading.gif'
import { t } from 'i18next'
import i18n from 'i18next'
import Flatpickr from 'react-flatpickr'
import { useNavigate, useLocation } from 'react-router-dom'
import { customDateFormat, customTimeFormat } from 'utils/common'
import moment from 'moment'
import { getPageRange } from 'utils/paginationHelper'
import { currentSessionDecoded, getAllowedRouteTypes } from 'helpers/auth-helper'
import { GET_USER_MEMBERSHIP } from 'graphql/membership/queries'

const sortOptions = [
    { name: 'Most Popular', href: '#', current: true },
    { name: 'Best Rating', href: '#', current: false },
    { name: 'Newest', href: '#', current: false },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Helper function to get trip status badge properties
const getTripStatusBadge = tripStatus => {
    const statusMap = {
        ONP: { color: 'blue', text: t('On Trip') },
        PND: { color: 'zinc', text: t('Pending') },
        CMP: { color: 'green', text: t('Completed') },
        CXL: { color: 'red', text: t('Cancelled') },
        PRC: { color: 'zinc', text: t('Processing Payment') },
        CNF: { color: 'emerald', text: t('Confirmed') },
        ACT: { color: 'emerald', text: t('Active') },
    }

    // Return the mapping or default for unknown statuses
    return statusMap[tripStatus] || { color: 'zinc', text: t('Unknown') }
}

// Helper function to check if booking is Day Pass type
const isDayPassBooking = booking => {
    return booking?.booking_type?.code === 'DYP'
}

// Helper function to get travel date for a booking
const getBookingTravelDate = booking => {
    if (isDayPassBooking(booking)) {
        // For Day Pass, get travel_date from passengers array
        return booking?.passengers?.[0]?.travel_date
    }
    // For regular bookings, get from trip_segments
    return booking?.trip_segments?.[0]?.travel_date
}

// Helper function to get passengers for a booking
const getBookingPassengers = booking => {
    if (isDayPassBooking(booking)) {
        // For Day Pass, passengers are at booking level
        return booking?.passengers || []
    }
    // For regular bookings, get from trip_segments
    return booking?.trip_segments?.[0]?.passengers || []
}

// Helper function to get passenger count
const getPassengerCount = booking => {
    return getBookingPassengers(booking).length
}

// Helper function to get first passenger info
const getFirstPassenger = booking => {
    const passengers = getBookingPassengers(booking)
    return passengers[0]?.passenger || null
}
export default function UserMembership({ isCheckInMode = false }) {
    const navigate = useNavigate()
    const location = useLocation()

    // Add isSearch state
    const [isSearch, setIsSearch] = useState(false)

    // Boat data
    const { filterObj: initialFilterObj } = location.state || {}
    const [filterObj, setFilterObj] = useState(
        initialFilterObj || {
            user_id: '',
            subscription_id: '',
            status: '',
            start_date: '',
            end_date: '',
            auto_renew: '',
        }
    )
    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmMessage, setConfirmMessage] = useState('')

    const [bookingList, setBookingList] = useState(null)
    const [bookingCount, setBookingCount] = useState(0)
    const [searchBookingStatus, setSearchBookingStatus] = useState('ACT')

    // Route type filtering
    const [routeTypeList, setRouteTypeList] = useState([])
    const [selectedRouteType, setSelectedRouteType] = useState('ALL')
    const [sessionData, setSessionData] = useState(null)
    const [allowedRouteType, setAllowedRouteType] = useState('ALL') // 'ALL', 'INL', or 'DMC'

    // Routes, Trips, and Locations filtering
    const [routes, setRoutes] = useState([])
    const [trips, setTrips] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedRouteIds, setSelectedRouteIds] = useState([])
    const [selectedTripIds, setSelectedTripIds] = useState([])
    const [startLocationId, setStartLocationId] = useState('')
    const [endLocationId, setEndLocationId] = useState('')

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(bookingCount / rowsPerPage)

    const [paginationGroup, setPaginationGroup] = useState(0)
    const pagesPerGroup = 10

    const handleGroupChange = newGroup => {
        setPaginationGroup(newGroup)
    }

    const handlePrevious = () => {
        if (page > 0) {
            const newPage = page - 1
            handlePageChange(newPage)
        }
    }

    const handleNext = () => {
        if (page < totalPages - 1) {
            const newPage = page + 1
            handlePageChange(newPage)
        }
    }

    useEffect(() => {
        if (initialFilterObj) {
            setFilterObj(initialFilterObj)
            setPage(0)
            setRowsPerPage(10)
            // Trigger search when returning from details page
            if (location.state?.filterObj) {
                setIsSearch(true)
                searchBookings() // Remove setTimeout and directly call searchBookings
            }
        } else {
            // Auto-search with current date range when page loads for the first time
            setIsSearch(true)
            searchBookings(0)
        }
    }, [initialFilterObj])

    const [getBookingList, { loading: bookingListLoading, error: bookingListError }] = useLazyQuery(
        GET_USER_MEMBERSHIP,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getUserSubscriptions.success && data.getUserSubscriptions.code === '200') {
                    setBookingList(data.getUserSubscriptions.user_subscriptions)
                    setBookingCount(data.getUserSubscriptions.total_count)
                }
            },
        }
    )

    const [getRouteTypeList, { loading: routeTypeListLoading }] = useLazyQuery(GET_ROUTE_TYPE, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.routeType?.length > 0) {
                setRouteTypeList(data.routeType)
            }
        },
    })

    // Get routes query
    const [getRoutes] = useLazyQuery(GET_ROUTES_FOR_REPORTS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getBoatRoute.success && data.getBoatRoute.code === '200') {
                setRoutes(data.getBoatRoute.boat_routes)
            }
        },
    })

    // Get locations query
    const [searchLocation] = useLazyQuery(SEARCH_LOCATION, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchLocation.success && data.searchLocation.code === '200') {
                setLocations(data.searchLocation.locations)
            }
        },
    })

    // Fetch session data and determine allowed route types on mount
    useEffect(() => {
        const fetchSessionData = async () => {
            try {
                const session = await currentSessionDecoded()
                if (session) {
                    setSessionData(session)
                    const allowed = getAllowedRouteTypes(session)
                    setAllowedRouteType(allowed)
                }
            } catch (error) {
                console.error('Error fetching session:', error)
            }
        }
        fetchSessionData()
        getRouteTypeList({ variables: { code: null } })
        // Fetch routes and locations
        getRoutes({ variables: { page: { offset: 0, limit: 100 }, statusCode: 'ACT' } })
        searchLocation({ variables: { page: { offset: 0, limit: 100 }, searchString: '' } })
    }, [])

    // When routes are selected, collect trips from all selected routes
    useEffect(() => {
        if (selectedRouteIds?.length > 0) {
            const selectedRoutes = routes.filter(r => selectedRouteIds.includes(r.route_id))
            const allTrips = selectedRoutes.flatMap(route => route.route_trip || [])
            // Remove duplicates based on trip_id
            const uniqueTrips = allTrips.filter(
                (trip, index, self) => index === self.findIndex(t => t.trip_id === trip.trip_id)
            )
            setTrips(uniqueTrips)
        } else {
            setTrips([])
            setSelectedTripIds([])
        }
    }, [selectedRouteIds, routes])

    // Filter route types based on user role
    const getFilteredRouteTypes = () => {
        if (!routeTypeList || routeTypeList.length === 0) return []

        if (allowedRouteType === 'ALL') {
            return routeTypeList
        }

        // Filter to only show allowed route type
        return routeTypeList.filter(type => type.code === allowedRouteType)
    }

    const filteredRouteTypes = getFilteredRouteTypes()

    // No client-side filtering needed - API handles route_type filtering

    const handleClearFilter = () => {
        setFilterObj({
            user_id: '',
            status: '',
            start_date: '',
            end_date: '',
            auto_renew: '',
        })
        getBookingList({ variables: {} }) // send no filters
        setSelectedRouteType('ALL')
        setSelectedRouteIds([])
        setSelectedTripIds([])
        setStartLocationId('')
        setEndLocationId('')
        setTrips([])
        setIsSearch(true)
        setBookingList(null)
        setPage(0)
        setPaginationGroup(0)
        // Automatically call API with current date range after clearing
        searchBookings(0)
    }

    // Get the route type to pass to API
    const getRouteTypeForApi = () => {
        // If user has restricted access (not 'ALL'), use their allowed route type
        if (allowedRouteType && allowedRouteType !== 'ALL') {
            return allowedRouteType
        }
        // If 'ALL' is selected, return null to fetch all route types
        if (selectedRouteType === 'ALL') {
            return null
        }
        // Use selected route type
        return selectedRouteType
    }

    // Search bookings Booking List
    const searchBookings = (currentPage = page) => {
        const currentDate = moment()
        const oneMonthAgo = moment().subtract(1, 'months')
        const offset = currentPage * rowsPerPage // Calculate correct offset based on current page
        const routeType = getRouteTypeForApi()

        // Build trip_location object only if both locations are selected (API requires non-null values)
        const tripLocation =
            startLocationId && endLocationId
                ? {
                      start_location_id: startLocationId,
                      end_location_id: endLocationId,
                  }
                : null

        getBookingList({
            variables: {
                filter: {
                    user_subscription_id: null,
                    user_id: filterObj.user_id || null,
                    subscription_id: null,
                    status_code: filterObj.status || null,
                    start_date_from: filterObj.start_date || null,
                    start_date_to: filterObj.end_date || null,
                },
                page: {
                    limit: rowsPerPage,
                    offset: offset, // Use calculated offset
                },
            },
        })
    }

    const handlePageChange = newPage => {
        setPage(newPage)
        setPaginationGroup(Math.floor(newPage / pagesPerGroup))
        searchBookings(newPage) // Pass the new page
    }

    const onClickSearch = () => {
        if (!filterObj.start_date || !filterObj.to_date) {
            setErrorMessage(t('Date range is mandatory'))
            setShowErrDialog(true)
            return
        }

        setPage(0)
        setPaginationGroup(0)
        setRowsPerPage(10)
        setIsSearch(true)
        searchBookings(0) // Pass initial page
    }

    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }
    const handleSucClose = () => {
        setShowSucDialog(false)
        setSuccessMessage('')
    }

    const handleCheckIn = booking => {
        // Navigate to check-in page with booking information
        navigate('/check-in/process', {
            state: {
                bookingInfo: booking,
                bookingId: booking.booking_id,
                travelDate: booking?.trip_segments?.[0]?.travel_date,
                segmentId: booking?.trip_segments?.[0]?.segment_id,
            },
        })
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                            <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                    <IdentificationIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                </div>
                                <div>
                                    <span className="ms-3">
                                        {isCheckInMode ? t('Check-in') : t('Manage User Membership')}
                                    </span>
                                    <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                        {isCheckInMode
                                            ? t(
                                                  'Process passenger check-ins, verify bookings, and manage boarding operations'
                                              )
                                            : t(
                                                  'Efficiently manage user memberships, view subscription details, and track membership status'
                                              )}
                                    </p>
                                </div>
                            </h1>
                        </div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                            {isCheckInMode && (
                                <button
                                    onClick={() => navigate('/check-in/qr-scanner')}
                                    className="inline-flex items-center gap-x-2 hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-4 py-2 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    <QrCodeIcon className="h-5 w-5" />
                                    {t('Scan QR Code')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {/* Search by User ID or Email */}
                <div className="sm:col-span-2">
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('Search')}</label>
                    <InputGroup>
                        <MagnifyingGlassIcon />
                        <input
                            type="text"
                            placeholder={t('User ID or Email')}
                            value={filterObj.user_id}
                            onChange={e => setFilterObj(prev => ({ ...prev, user_id: e.target.value }))}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    setPage(0)
                                    searchBookings(0)
                                }
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                        />
                    </InputGroup>
                </div>

                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">{t('Status')}</label>
                    <select
                        value={filterObj.status}
                        onChange={e => {
                            const value = e.target.value
                            setFilterObj(prev => ({ ...prev, status: value }))
                            // setPage(0)
                            // searchBookings(0, { status: value })
                        }}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    >
                        <option value="">{t('All')}</option>
                        <option value="CNF">{t('Confirmed')}</option>
                        <option value="PND">{t('Pending')}</option>
                        <option value="ACT">{t('Active')}</option>
                        <option value="INA">{t('Inactive')}</option>
                        <option value="PRC">{t('Processing Payment')}</option>
                        <option value="CXL">{t('Cancelled')}</option>
                        <option value="TRC">{t('Trip Cancelled')}</option>
                        <option value="NOS">{t('No Show')}</option>
                        <option value="RSC">{t('Rescheduled')}</option>
                    </select>
                </div>

                {/* Date Range */}
                <div>
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Subscription Date Range')}
                    </label>
                    <Flatpickr
                        options={{ mode: 'range', dateFormat: 'Y-m-d' }}
                        value={[filterObj.start_date, filterObj.end_date]}
                        onChange={date => {
                            const start = date[0] ? date[0].toISOString().split('T')[0] : ''
                            const end = date[1] ? date[1].toISOString().split('T')[0] : ''
                            setFilterObj(prev => ({ ...prev, start_date: start, end_date: end }))
                        }}
                        placeholder={t('From - To')}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                </div>
            </div>

            {/* Search & Clear buttons */}
            <div className="flex gap-2 items-end">
                <button
                    type="button"
                    onClick={() => {
                        setPage(0)
                        searchBookings(0)
                    }}
                    className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                >
                    {t('Search')}
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setFilterObj({ user_id: '', status: '', start_date: '', end_date: '' })
                        setPage(0)
                        searchBookings(0)
                    }}
                    className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                    {t('Clear')}
                </button>
            </div>

            {/* Booking List */}
            {bookingListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {bookingListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No trips found')}</div>
                </div>
            )}
            {!bookingListLoading && !bookingListError && isSearch && (!bookingList || bookingList?.length === 0) && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {isSearch && !bookingListLoading && !bookingListError && bookingList?.length > 0 && (
                <div>
                    <div className="bg-white">
                        {bookingList.map(booking => (
                            <div key={booking.booking_id} className="mt-5">
                                <h2 className="sr-only">{t('Recent orders')}</h2>
                                <div className="w-full grid grid-cols-1 gap-4">
                                    <div className="max-w-full space-y-8 sm:px-4 lg:max-w-full lg:px-0">
                                        <div className="border-b border-t border-gray-200 shadow-sm sm:rounded-lg sm:border bg-slate-50">
                                            <div className="relative items-center border-b border-gray-200 px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 md:gap-y-3 p-2 pb-0">
                                                <>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('Start Date')}</dd>
                                                        <dt className="font-medium text-gray-900">
                                                            {booking?.start_date
                                                                ? moment(booking.start_date).format('DD MMM YYYY')
                                                                : 'N/A'}
                                                        </dt>
                                                    </div>

                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('End Date')}</dd>
                                                        <dt className="font-medium text-gray-900">
                                                            {booking?.end_date
                                                                ? moment(booking.end_date).format('DD MMM YYYY')
                                                                : 'N/A'}
                                                        </dt>
                                                    </div>
                                                </>
                                                <>
                                                    <div>
                                                        <dd className="mt-1 text-gray-500">{t('Remain')}</dd>
                                                        <dt className="font-medium text-gray-900">
                                                            {booking?.days_remaining} {t('Days')}
                                                        </dt>
                                                    </div>
                                                </>

                                                <div>
                                                    <dd className="mt-1 text-gray-500">{t('Category')}</dd>
                                                    <dt className="font-medium text-gray-900">
                                                        {booking?.subscription_plan?.category?.description?.[
                                                            i18n?.language?.toLowerCase() || 'en'
                                                        ] ||
                                                            booking?.subscription_plan?.category_code ||
                                                            'N/A'}
                                                    </dt>
                                                </div>

                                                <div className="text-end md:col-span-2 xl:col-span-1 flex items-end flex-col gap-y-1">
                                                    <p className="font-extrabold mt-7 flex justify-normal items-center gap-x-2 text-[20px] text-primary mb-2">
                                                        <dd className="mt-1 text-gray-500 text-sm font-[400]">
                                                            {t('Membership Cost')}
                                                        </dd>{' '}
                                                        {booking?.subscription_plan?.amount}{' '}
                                                        {booking?.subscription_plan?.currency}
                                                    </p>
                                                </div>
                                                <dl className="grid flex-1 grid-cols-2 gap-x-6 text-sm sm:col-span-3 sm:grid-cols-3 lg:col-span-2"></dl>
                                            </div>
                                            <h4 className="sr-only">Items</h4>
                                            <ul className="divide-y divide-gray-200">
                                                <li className="p-4">
                                                    <div className="flex sm:items-start">
                                                        <div className="flex-1 text-sm">
                                                            <div className="text-gray-900 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                                                <div className="shrink-0 ">
                                                                    <div className="flex items-center">
                                                                        <IdentificationIcon className="w-[50px] h-[50px] text-white rounded-full bg-primary p-3" />
                                                                        <div className="ps-3 pt-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <h5 className="text-[18px]">
                                                                                    {
                                                                                        booking?.subscription_plan
                                                                                            ?.subscription_name?.en
                                                                                    }
                                                                                </h5>
                                                                            </div>
                                                                            <p className="text-[14px] border-gray-300 sm:mt-2">
                                                                                <>
                                                                                    {t('Remain')} |{' '}
                                                                                    {booking?.days_remaining}{' '}
                                                                                    {t('Days')}
                                                                                </>
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex shrink-0 md:place-content-end lg:place-content-center">
                                                                    <div className="flex items-center ">
                                                                        <UserIcon
                                                                            aria-hidden="true"
                                                                            className="h-5 w-5 text-green-500"
                                                                        />
                                                                        <p className="ml-2 text-sm font-100 text-gray-500">
                                                                            {t('Member Status')} <br /> {''}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex items-center ms-4 border-s-[1px] ps-3 border-gray-300 border-solid">
                                                                        {booking?.status ? (
                                                                            <span
                                                                                className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${(() => {
                                                                                    const badge = getTripStatusBadge(
                                                                                        booking.status
                                                                                    )
                                                                                    switch (badge.color) {
                                                                                        case 'red':
                                                                                            return 'bg-red-100 text-red-800'
                                                                                        case 'emerald':
                                                                                            return 'bg-emerald-100 text-emerald-800'
                                                                                        case 'blue':
                                                                                            return 'bg-blue-100 text-blue-800'
                                                                                        case 'yellow':
                                                                                            return 'bg-yellow-100 text-yellow-800'
                                                                                        case 'purple':
                                                                                            return 'bg-purple-100 text-purple-800'
                                                                                        default:
                                                                                            return 'bg-zinc-100 text-zinc-800'
                                                                                    }
                                                                                })()}`}
                                                                            >
                                                                                {
                                                                                    getTripStatusBadge(booking.status)
                                                                                        .text
                                                                                }
                                                                            </span>
                                                                        ) : (
                                                                            <span className="italic text-gray-400">
                                                                                N/A
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col md:col-span-2 xl:col-span-1 md:place-content-end lg:items-end lg:place-items-end">
                                                                    <Badge
                                                                        color={
                                                                            booking.status?.description?.en ===
                                                                            'Confirmed'
                                                                                ? 'emerald'
                                                                                : booking.status?.description?.en ===
                                                                                    'Payment Processing'
                                                                                  ? 'lime'
                                                                                  : booking.status?.description?.en ===
                                                                                      'Cancelled'
                                                                                    ? 'red'
                                                                                    : 'zinc'
                                                                        }
                                                                        className="text-end w-fit mb-3"
                                                                    >
                                                                        {booking.status?.description?.en}
                                                                    </Badge>
                                                                    <div className="flex flex-col gap-2 lg:w-[250px]">
                                                                        <button
                                                                            className="bg-primary text-white hover:bg-primary/[0.9] py-2 px-8 rounded flex-none"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/user-membership/details/${booking?.user_subscription_id}`
                                                                                )
                                                                            }
                                                                        >
                                                                            {t('View details')}
                                                                        </button>
                                                                        {/* Check-in button - show if allow_check_in is true for any segment and isCheckInMode is true */}
                                                                        {isCheckInMode &&
                                                                            (booking?.trip_segments?.some(
                                                                                segment => segment.allow_check_in
                                                                            ) ||
                                                                                booking?.trip_segments?.some(
                                                                                    segment =>
                                                                                        !segment.allow_check_in &&
                                                                                        segment.passengers?.some(
                                                                                            p =>
                                                                                                p.check_in?.status
                                                                                                    ?.code === 'CHK'
                                                                                        )
                                                                                )) &&
                                                                            booking?.trip_segments?.[0]?.route
                                                                                ?.master_route?.route_type?.code !==
                                                                                'DMC' &&
                                                                            (() => {
                                                                                // Check if any passenger in any segment is checked in
                                                                                const hasCheckedInPassengers =
                                                                                    booking?.trip_segments?.some(
                                                                                        segment =>
                                                                                            segment.passengers?.some(
                                                                                                p =>
                                                                                                    p.check_in?.status
                                                                                                        ?.code === 'CHK'
                                                                                            )
                                                                                    )

                                                                                return (
                                                                                    <button
                                                                                        className="text-white hover:opacity-90 py-2 px-8 rounded flex-none"
                                                                                        style={{
                                                                                            backgroundColor:
                                                                                                'rgb(40 95 116 / var(--tw-bg-opacity, 1))',
                                                                                        }}
                                                                                        onClick={() =>
                                                                                            handleCheckIn(booking)
                                                                                        }
                                                                                    >
                                                                                        {hasCheckedInPassengers
                                                                                            ? t('View Check-In')
                                                                                            : t('Check-In')}
                                                                                    </button>
                                                                                )
                                                                            })()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious onClick={handlePrevious} disabled={page === 0} />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext onClick={handleNext} disabled={page >= totalPages - 1} />
                    </Pagination>
                    <Divider />
                </div>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleSucClose} />
            )}
        </>
    )
}
